import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { isPlatformChromeTenant } from '../../../app/platformChromeTenant.js';
import { useCustomerAnalytics } from '../../../shared/analytics/CustomerAnalyticsProvider.js';
import { emitCustomerAnalyticsOnTenant } from '../../../shared/analytics/customerAnalyticsClient.js';
import { useApiClient } from '../../../shared/providers/AppDataProvider.js';
import {
  ANALYTICS_ACCOUNT_EVENTS,
} from 'pi-kiosk-shared';
import { buildScreenMetadata } from 'pi-kiosk-shared/analytics/metadataBuilders';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import { isGloballySignedInChrome } from '../../hub/customerHubPaths.js';
import {
  canDownloadInternalReceipt,
  downloadAccountReceiptPdf,
  onDownloadAuthFailure,
  receiptDownloadFilename,
  resolveReceiptDownloadUserMessageKey,
  triggerBlobDownload,
  type ReceiptRowLike,
  ReceiptDownloadHttpError,
} from '../accountReceiptDownload.js';
import {
  isReceiptDownloadBlocked,
  markReceiptDownloadBlocked,
} from '../receiptDownloadBlockedStore.js';
import { receiptsLog } from '../logging.js';

function receiptStatusHint(
  row: ReceiptRowLike,
  t: (key: string) => string
): string {
  if (row.receiptStatus === 'pending') {
    return t('account.receipt.pending');
  }
  if (row.receiptStatus === 'failed') {
    return t('account.receipt.failed');
  }
  if (row.receiptAvailability === 'EXTERNAL_PROVIDER') {
    if (row.receiptKind === 'DONATION_CONFIRMATION') {
      return t('account.receipt.donationExternalProvider');
    }
    return t('account.receipt.externalProvider');
  }
  return t('account.receipt.unavailable');
}

function downloadLabel(row: ReceiptRowLike, t: (key: string) => string): string {
  if (row.receiptKind === 'DONATION_CONFIRMATION') {
    return t('account.receipt.donationConfirmationPdf');
  }
  return t('account.receipt.downloadPdf');
}

function resolveReceiptActionShellClass(
  block: boolean,
  fullWidthOnNarrow: boolean,
): string {
  if (block) {
    return 'flex w-full min-h-[44px] flex-col justify-center gap-1 sm:min-h-0';
  }
  if (fullWidthOnNarrow) {
    return 'flex w-full min-h-[44px] flex-col justify-center gap-1 sm:min-h-0 sm:w-auto sm:items-end';
  }
  return 'flex min-h-[44px] flex-col justify-center gap-1 sm:min-h-0 sm:items-end';
}

function resolveReceiptButtonClass(block: boolean, fullWidthOnNarrow: boolean): string {
  if (block) {
    return 'min-h-[44px] w-full sm:min-h-0';
  }
  if (fullWidthOnNarrow) {
    return 'min-h-[44px] w-full sm:min-h-0 sm:w-auto';
  }
  return 'min-h-[44px] sm:min-h-0';
}

function resolveDownloadScopeTenant(
  row: ReceiptRowLike,
  routeTenantCode: string,
): string {
  return row.tenantCode ?? routeTenantCode;
}

export function AccountReceiptDownloadButton({
  row,
  fullWidthOnNarrow = false,
  block = false,
  screenName = 'account_payments',
}: {
  row: ReceiptRowLike;
  fullWidthOnNarrow?: boolean;
  /** Full-width button in stacked action columns (Objednávky). */
  block?: boolean;
  screenName?: string;
}): JSX.Element | null {
  const { t } = useTranslation('customer');
  // Download via the current slug; backend authorizes by customerId (cross-tenant).
  const tenantCode = useTenantCode();
  const scopeTenant = resolveDownloadScopeTenant(row, tenantCode);
  const platformChrome = isPlatformChromeTenant(tenantCode);
  // G1 — same signed-in chrome as nav/header; dies when /me clears session memory.
  // SoftRace: live session still counts while sessionHydrated briefly flips false.
  // Pre-hydrate guest (null + !hydrated): hide proactively — no show→fail→hide.
  const { session, sessionHydrated } = useCustomerSession();
  const signedInChrome = isGloballySignedInChrome({ session });
  const canDownload = signedInChrome && (sessionHydrated || session !== null);
  const runtime = useRuntimeConfig();
  const analytics = useCustomerAnalytics();
  const api = useApiClient();
  const [pending, setPending] = useState(false);
  /**
   * After a hard failure, stop offering a dead Download control.
   * Seeded from durable store so remount/refetch does not resurrect the CTA (G2).
   */
  const [failureBlockedKey, setFailureBlockedKey] = useState<string | null>(null);
  const inFlightRef = useRef(false);

  const rowBlockKey = `${scopeTenant}|${row.transactionId}|${row.downloadPath ?? ''}|${row.receiptStatus ?? ''}|${row.receiptAvailability ?? ''}`;
  const metaBlocked = isReceiptDownloadBlocked(scopeTenant, row.transactionId, row);
  const downloadBlocked = metaBlocked || failureBlockedKey === rowBlockKey;

  const handleDownload = useCallback(async () => {
    if (!canDownload || inFlightRef.current || downloadBlocked) {
      return;
    }
    inFlightRef.current = true;
    setPending(true);
    try {
      // Platform chrome: cookie-first absolute PDF; tenant path uses cookie XOR.
      const { blob, filename } = await downloadAccountReceiptPdf({
        apiBaseUrl: runtime.apiBaseUrl,
        tenantCode: scopeTenant,
        accessToken: null,
        transactionId: row.transactionId,
        downloadPath: row.downloadPath,
        ...(platformChrome ? { platformAbsolute: true } : {}),
      });
      triggerBlobDownload(blob, filename ?? receiptDownloadFilename(row));
      // Keep ANALYTICS_* + track/emit in one call site (analyticsEmitterManifest.grep).
      const downloadedMeta = {
        screenName,
        metadata: {
          ...buildScreenMetadata({ screenName }),
          receipt_format: 'pdf',
          receipt_kind: row.receiptKind ?? 'PRODUCT_RECEIPT',
        },
      };
      if (platformChrome && row.tenantCode !== undefined && row.tenantCode.trim().length > 0) {
        void emitCustomerAnalyticsOnTenant(api, row.tenantCode, {
          eventName: ANALYTICS_ACCOUNT_EVENTS.RECEIPT_DOWNLOADED,
          ...downloadedMeta,
        });
      } else {
        analytics.track({
          eventName: ANALYTICS_ACCOUNT_EVENTS.RECEIPT_DOWNLOADED,
          ...downloadedMeta,
        });
      }
    } catch (error: unknown) {
      const messageKey = resolveReceiptDownloadUserMessageKey(error);
      receiptsLog.warn('Receipt PDF download failed', {
        operation: 'downloadReceiptPdf',
        transactionId: row.transactionId,
        messageKey,
        ...(error instanceof ReceiptDownloadHttpError
          ? { statusCode: error.statusCode }
          : { reason: error instanceof Error ? error.name : 'unknown' }),
      });
      const isAuthFailure =
        error instanceof ReceiptDownloadHttpError &&
        (error.statusCode === 401 || error.statusCode === 403);
      if (isAuthFailure) {
        // G1 — end session (download helper also calls this when not mocked).
        // CTA disappears via chrome signal, not remount-local state alone.
        void onDownloadAuthFailure();
      }
      // G2 durable block for remount hide (auth + non-auth); do not fight W2 store.
      markReceiptDownloadBlocked(scopeTenant, row.transactionId, row.downloadPath);
      setFailureBlockedKey(rowBlockKey);
    } finally {
      inFlightRef.current = false;
      setPending(false);
    }
  }, [
    analytics,
    api,
    canDownload,
    downloadBlocked,
    platformChrome,
    row,
    rowBlockKey,
    runtime.apiBaseUrl,
    scopeTenant,
    screenName,
    setFailureBlockedKey,
    setPending,
  ]);

  const actionClass = resolveReceiptActionShellClass(block, fullWidthOnNarrow);

  const downloadable = canDownloadInternalReceipt(row) && canDownload && !downloadBlocked;

  if (!downloadable) {
    // No session, no downloadPath, or prior hard failure: never show a dead Download PDF.
    if (downloadBlocked || (canDownloadInternalReceipt(row) && !canDownload)) {
      return null;
    }
    const hasActionableStatus =
      row.receiptStatus === 'pending' ||
      row.receiptStatus === 'failed' ||
      row.receiptAvailability === 'EXTERNAL_PROVIDER';
    if (!hasActionableStatus) {
      return null;
    }
    return (
      <div className={actionClass} data-testid="account-receipt-status">
        <span className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] px-3 py-2 text-center text-xs text-[var(--color-on-surface)] sm:text-right">
          {receiptStatusHint(row, t)}
        </span>
      </div>
    );
  }

  const label = downloadLabel(row, t);

  return (
    <div className={actionClass}>
      <Button
        intent="secondary"
        size="md"
        className={resolveReceiptButtonClass(block, fullWidthOnNarrow)}
        disabled={pending}
        data-testid="customer-receipt-download-enabled"
        onClick={() => {
          void handleDownload();
        }}
      >
        {pending ? t('account.receipt.downloading') : label}
      </Button>
    </div>
  );
}
